import * as React from "react";
import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  useMediaQuery,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import { LoadingButton } from "@mui/lab";
import PropTypes from "prop-types";
import { useFormik } from "formik";
import * as Yup from "yup";
import axios from "axios";
import { useDispatch } from "react-redux";
import { useTheme } from "@mui/material/styles";
import { useGlobalAuthContext } from "../../context/AuthContext";
import { useUserProfileSelector } from "../../hooks/useUserProfile";
import { DynamicAlert } from "../../utils/Alerts";
import { apiRoomNate } from "../../Slices/roomNateApis";
import { setUserProfile } from "../../Slices/roomNateSlice";
import { getAuthorizationHeaderObject } from "../../utils/authHeaders";
import {
  getAuthGlassFieldSx,
  getAuthPrimaryButtonSx,
} from "../Auth/authGlassStyles";
import { shapeShell } from "../../theme/shape";

/**
 * Account details editor: name and (read-only) email.
 * Opened from the /main header menu → "Account".
 */
export default function AccountEditDialog({ open, onClose }) {
  const theme = useTheme();
  const fullScreenDialog = useMediaQuery(theme.breakpoints.down("sm"));
  const dispatch = useDispatch();
  const { currentUserSessionStorage } = useGlobalAuthContext();
  const { userProfile: profile } = useUserProfileSelector();
  const fieldSx = getAuthGlassFieldSx(theme);

  const [load, setLoad] = React.useState(false);
  const [alertTitle, setAlertTitle] = React.useState("");
  const [alertStatus, setAlertStatus] = React.useState(false);

  const authHeaders = () =>
    getAuthorizationHeaderObject(currentUserSessionStorage);

  const formik = useFormik({
    initialValues: {
      name: profile?.name || "",
      email: profile?.email || "",
    },
    enableReinitialize: true,
    validationSchema: Yup.object({
      name: Yup.string().required("Name is required"),
    }),
    onSubmit: async (values) => {
      setLoad(true);
      try {
        await axios.put(
          "/user/update/",
          { name: values.name },
          { headers: authHeaders() }
        );
        dispatch(apiRoomNate.util.invalidateTags(["UserProfile"]));
        const { data } = await axios.get("user/getLoggedInUser", {
          headers: authHeaders(),
        });
        if (data) dispatch(setUserProfile(data));
        setLoad(false);
        onClose();
      } catch (error) {
        setLoad(false);
        const msg =
          typeof error?.response?.data === "string"
            ? error.response.data
            : "Could not save changes.";
        setAlertTitle(msg);
        setAlertStatus(false);
        setTimeout(() => setAlertTitle(""), 2000);
      }
    },
  });

  const resetAndClose = () => {
    formik.resetForm();
    onClose();
  };

  const handleDialogClose = (_event, reason) => {
    if (load) return;
    if (reason === "backdropClick" || reason === "escapeKeyDown") {
      resetAndClose();
    }
  };

  return (
    <>
      {alertTitle?.length >= 1 && (
        <DynamicAlert status={alertStatus} title={alertTitle} />
      )}

      <Dialog
        open={open}
        onClose={handleDialogClose}
        fullScreen={fullScreenDialog}
        fullWidth
        maxWidth="sm"
        scroll="paper"
        aria-labelledby="account-edit-dialog-title"
          sx={{
          "& .MuiDialog-paper": {
            ...(fullScreenDialog && { maxHeight: "100dvh" }),
            ...(!fullScreenDialog && { borderRadius: shapeShell }),
          },
          "& .MuiDialogContent-root": {
            WebkitOverflowScrolling: "touch",
          },
        }}
      >
        <DialogTitle
          id="account-edit-dialog-title"
          sx={{
            fontWeight: 600,
            letterSpacing: "-0.02em",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            p: { xs: 2.5, sm: 3 },
          }}
        >
          Account
          <IconButton
            aria-label="Close"
            onClick={resetAndClose}
            disabled={load}
            size="small"
            sx={{ color: "text.secondary", ml: 1.5 }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>

        <DialogContent
          sx={{
            px: { xs: 2, sm: 3 },
            pb: {
              xs: "max(env(safe-area-inset-bottom, 0px) + 24px, 24px)",
              sm: 3,
            },
            pt: 0,
          }}
        >
          <Stack
            gap={2}
            component="form"
            onSubmit={formik.handleSubmit}
            sx={{ mt: 1 }}
          >
            <TextField
              required
              label={
                formik.touched.name && formik.errors.name
                  ? formik.errors.name
                  : "Name"
              }
              fullWidth
              variant="outlined"
              id="name"
              type="text"
              inputMode="text"
              autoComplete="name"
              value={formik.values.name}
              name="name"
              onChange={formik.handleChange}
              error={formik.touched.name && Boolean(formik.errors.name)}
              onBlur={formik.handleBlur}
              InputLabelProps={
                formik.touched.name && formik.errors.name
                  ? { shrink: true }
                  : undefined
              }
              sx={fieldSx}
            />

            <TextField
              label="Email"
              fullWidth
              variant="outlined"
              id="email"
              type="email"
              value={formik.values.email}
              InputProps={{
                readOnly: true,
                endAdornment: (
                  <InputAdornment position="end">
                    <LockOutlinedIcon
                      sx={{ fontSize: 16, color: "text.disabled" }}
                    />
                  </InputAdornment>
                ),
              }}
              sx={[
                fieldSx,
                {
                  "& .MuiOutlinedInput-root": {
                    pointerEvents: "none",
                    "& fieldset": {
                      borderColor: "rgba(0,0,0,0.10) !important",
                    },
                  },
                  "& .MuiOutlinedInput-input": {
                    WebkitTextFillColor: "rgba(0,0,0,0.40) !important",
                    color: "rgba(0,0,0,0.40) !important",
                  },
                  "& .MuiInputLabel-root": {
                    color: "rgba(0,0,0,0.38) !important",
                  },
                },
              ]}
            />

            <Box mt={1}>
              <LoadingButton
                loading={load}
                variant="contained"
                fullWidth
                type="submit"
                sx={getAuthPrimaryButtonSx(theme)}
              >
                Save details
              </LoadingButton>
            </Box>
          </Stack>
        </DialogContent>
      </Dialog>
    </>
  );
}

AccountEditDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
};
